/**
 * AuthContext — single source of truth for authentication state.
 *
 * The query trpc.auth.me is called EXACTLY ONCE here, at the top of the
 * component tree. All components (Home, AuthGuard, BolsoLayout, etc.) read
 * auth state via useAuthContext() instead of calling useAuth() directly.
 *
 * This prevents the insertBefore error: when multiple components call
 * useAuth() simultaneously, React may try to reconcile two different
 * instances of the same query during the same commit, causing DOM corruption.
 */
import { createContext, useContext, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { trpc } from "@/lib/trpc";
import { TRPCClientError } from "@trpc/client";
import type { User } from "../../../drizzle/schema";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  isAuthenticated: false,
  logout: () => {},
  refresh: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const utils = trpc.useUtils();

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      throw error;
    } finally {
      utils.auth.me.setData(undefined, null);
      await utils.auth.me.invalidate();
    }
  }, [logoutMutation, utils]);

  const refresh = useCallback(() => {
    meQuery.refetch();
  }, [meQuery]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void meQuery.refetch();
    };
    window.addEventListener("pageshow", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);
    return () => {
      window.removeEventListener("pageshow", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
    };
  }, [meQuery.refetch]);

  // Persist user info in localStorage for offline/cold-start reads
  useEffect(() => {
    try {
      localStorage.setItem(
        "manus-runtime-user-info",
        JSON.stringify(meQuery.data ?? null)
      );
    } catch {
      // ignore quota or private-mode errors
    }
  }, [meQuery.data]);

  const value = useMemo<AuthContextValue>(() => ({
    user: meQuery.data ?? null,
    loading: meQuery.isLoading || logoutMutation.isPending,
    isAuthenticated: Boolean(meQuery.data),
    logout,
    refresh,
  }), [
    meQuery.data,
    meQuery.isLoading,
    logoutMutation.isPending,
    logout,
    refresh,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}

// Keep backward-compat export for components that import AuthContext directly
export { AuthContext };
